"use client";
import React from "react";
import NavBarV2 from "@/components/global/partials/HeaderV2";
import BirthdayTopBanner from "@/components/global/BirthdayTopBanner";
import BirthdayPopUp from "@/components/global/BirthdayPopUp";
import SummerSaleTopBanner from "@/components/global/SummerSaleTopBanner";
import SummerSalePopup from "@/components/global/SummerSalePopup";
import AffiliateBanners from "@/components/global/AffiliateBanners";
import LaunchEventWidget from "@/components/global/LaunchEventWidget";
import TradingCompetitionPopup from "@/components/global/TradingCompetitionPopup";
import { useLaunchEventLive } from "@/hooks/useLaunchEventLive";
import type { PageAffiliateOldDesignSchema } from "@/sanity/pages/PageAffiliateOldDesignTemplate";
import {
  applyAffiliateDiscountToLabel,
  applyAffiliateDiscountToRichtext,
  applyAffiliateDiscountToText,
  getAffiliateDiscountOverrides,
  isAffiliateDiscountActive,
  transformAffiliateDealTable,
  withAffiliateOfferDiscount,
} from "@/lib/affiliateDiscount";
import type { AffiliateFundingType } from "@/lib/affiliateDiscount";
import {
  getNewOfferPercent,
  newOfferBadgeText,
  newOfferHeroContent,
  resolveOfferLocale,
  rewriteCouponRichtext,
  rewriteOfferChip,
  rewriteOfferListItem,
  rewriteOfferRichtext,
  type OfferLocale,
} from "@/lib/affiliateNewOffer";
import Hero from "../pageComponents/affiliate-old-design/Hero";
import ExclusiveSection from "../pageComponents/affiliate-old-design/Exclusive";
import Deals from "../pageComponents/affiliate-old-design/Deals";
import {
  PricingHeroBlock,
  PricingTiersBlock,
  PricingComparisonBlock,
  PricingPlatformsBlock,
} from "../pageComponents/pricingSectionV2";
import NewPricingSection from "../pageComponents/newPricing/NewPricingSection";
import { PROFIT_SPLIT_FALLBACK, resolveProfitSplit } from "@/lib/profitSplit";
import Rules from "../pageComponents/affiliate-old-design/Rules";
import Payouts from "../pageComponents/affiliate-old-design/Payouts";
import ReviewsSection from "../pageComponents/challenge/Reviews";
import StepsSection from "../pageComponents/affiliate-old-design/Steps";
import LastCTACompetitionSection from "../pageComponents/comp-affiliate-old-design/LastCTACompetition";
import OldFooter from "../global/partials/OldFooter";
import TopBanner from "../global/TopBanner";

// Swaps the discount percentages embedded in the page copy for the global
// "Affiliate Discount Settings" values when the toggle is active. Fields keep
// their page value whenever the toggle is off or a global field is empty.
// The deal-table prices are always computed dynamically from the regular-price
// column; only the percent source switches between the column header (page
// fallback) and the global settings. The one exception is the $10,000 starter
// challenge on crypto/CFD tables, which keeps its Sanity price untouched.
function withAffiliateDiscountOverrides(
  data: PageAffiliateOldDesignSchema,
): PageAffiliateOldDesignSchema {
  const settings = data?.affiliateDiscountSettings;
  const fundingType =
    data?.affiliatesTablesRef?.name === "Futures" ? "futures" : "crypto";
  const overrides = {
    ...getAffiliateDiscountOverrides(settings, fundingType),
    profitSplit: resolveProfitSplit(data?.profitSplit) ?? PROFIT_SPLIT_FALLBACK,
  };
  const isActive = isAffiliateDiscountActive(settings);

  const challengeSetting =
    fundingType === "futures"
      ? settings?.futuresChallengeDiscountPercent
      : settings?.cryptoChallengeDiscountPercent;
  const instantFundingSetting =
    fundingType === "futures"
      ? settings?.futuresInstantFundingDiscountPercent
      : settings?.cryptoInstantFundingDiscountPercent;

  const affiliatesTablesRef = data.affiliatesTablesRef
    ? {
        ...data.affiliatesTablesRef,
        dealsItems: data.affiliatesTablesRef.dealsItems
          ? data.affiliatesTablesRef.dealsItems.map((item) => {
              const isInstantFunding = /instant/i.test(item.title ?? "");
              const isChallenge = /challenge/i.test(item.title ?? "");
              const settingsPercent = isInstantFunding
                ? instantFundingSetting
                : isChallenge
                  ? challengeSetting
                  : undefined;
              const cardValue = isInstantFunding
                ? overrides.instantFunding
                : isChallenge
                  ? overrides.challenge
                  : undefined;
              return {
                ...item,
                chip:
                  cardValue != null
                    ? applyAffiliateDiscountToLabel(item.chip, cardValue)
                    : item.chip,
                callout:
                  cardValue != null
                    ? applyAffiliateDiscountToLabel(item.callout, cardValue)
                    : item.callout,
                table: transformAffiliateDealTable(
                  item.table,
                  settingsPercent,
                  isActive,
                  fundingType,
                ),
              };
            })
          : data.affiliatesTablesRef.dealsItems,
      }
    : data.affiliatesTablesRef;

  if (!overrides.isActive) return { ...data, affiliatesTablesRef };

  const text = <T extends string | undefined | null>(value: T): T =>
    applyAffiliateDiscountToText(value, overrides);
  const rich = <
    T extends PageAffiliateOldDesignSchema["heroText"] | undefined | null,
  >(
    value: T,
  ): T => applyAffiliateDiscountToRichtext(value, overrides) as T;

  return {
    ...data,
    heroChip1: data.heroChip1
      ? { ...data.heroChip1, text: text(data.heroChip1.text) }
      : data.heroChip1,
    heroChip2: data.heroChip2
      ? { ...data.heroChip2, text: text(data.heroChip2.text) }
      : data.heroChip2,
    heroText: rich(data.heroText),
    heroContent: rich(data.heroContent),
    heroCallout: rich(data.heroCallout),
    heroTextBadge1: text(data.heroTextBadge1),
    heroTextBadge2: text(data.heroTextBadge2),
    heroItems: data.heroItems
      ? data.heroItems.map((item) => ({ ...item, text: text(item.text) }))
      : data.heroItems,
    heroRecommendation: data.heroRecommendation
      ? { ...data.heroRecommendation, text: text(data.heroRecommendation.text) }
      : data.heroRecommendation,
    // Every language of each global row, since the Hero picks one only later.
    affiliateHeroItems: data.affiliateHeroItems
      ? {
          ...data.affiliateHeroItems,
          items: data.affiliateHeroItems.items?.map((item) => ({
            ...item,
            text: text(item.text),
            textI18n: item.textI18n && {
              de: text(item.textI18n.de),
              br: text(item.textI18n.br),
            },
          })),
        }
      : data.affiliateHeroItems,

    exclusiveTitle: rich(data.exclusiveTitle),
    exclusiveTestimonials: data.exclusiveTestimonials
      ? data.exclusiveTestimonials.map((item) => ({
          ...item,
          text: rich(item.text),
        }))
      : data.exclusiveTestimonials,
    exclusiveItems: data.exclusiveItems
      ? data.exclusiveItems.map((item) => ({ ...item, text: text(item.text) }))
      : data.exclusiveItems,

    dealsTitle: rich(data.dealsTitle),
    affiliatesTablesRef,

    stepsText: rich(data.stepsText),
    stepsItems: data.stepsItems
      ? data.stepsItems.map((item) => ({ ...item, text: rich(item.text) }))
      : data.stepsItems,

    lastChip: text(data.lastChip),
    lastText: rich(data.lastText),
  };
}

/**
 * On affiliate pages every purchase must go through the partner's own hero CTA
 * link — it carries the `?aff=` / coupon params that attribute the sale. So we
 * rewrite the href of every tier CTA (challenge, specs-toggle secondary and
 * funded) to that URL, keeping titles, variants and everything else from the
 * shared Pricing Section V2 document.
 *
 * This is a page-level override on a copy of the data: the Sanity document is
 * untouched and keeps its per-product checkout links for every other page.
 */
function withAffiliatePricingCta(
  section: PageAffiliateOldDesignSchema["pricingSectionV2"],
  url?: string,
): PageAffiliateOldDesignSchema["pricingSectionV2"] {
  if (!section || !url) return section;

  const withHref = <T extends { href?: string } | undefined>(cta: T): T =>
    cta ? ({ ...cta, href: url } as T) : cta;

  return {
    ...section,
    assets: section.assets?.map((asset) => ({
      ...asset,
      categories: asset.categories?.map((category) => ({
        ...category,
        tiers: category.tiers?.map((tier) => ({
          ...tier,
          ctaPrimary: withHref(tier.ctaPrimary),
          secondaryCtaPrimary: withHref(tier.secondaryCtaPrimary),
          fundedCtaPrimary: withHref(tier.fundedCtaPrimary),
        })),
      })),
    })),
  };
}

interface NewOffer {
  percent: number;
  locale: OfferLocale;
  fundingType: AffiliateFundingType;
}

// The percentage and language driving the unified offer copy, or undefined when
// the page should keep its stored wording — either because no discount is set
// for this funding type (see withAffiliateOfferDiscount), or because the page holds
// none of the old split-discount wording to rewrite.
function resolveNewOffer(
  data: PageAffiliateOldDesignSchema,
): NewOffer | undefined {
  const fundingType: AffiliateFundingType =
    data?.affiliatesTablesRef?.name === "Futures" ? "futures" : "crypto";
  const percent = getNewOfferPercent(data?.pricingV2Discount, fundingType);
  if (percent == null) return undefined;
  const locale = resolveOfferLocale([
    data?.heroChip1?.text,
    ...(data?.exclusiveItems ?? []).map((item) => item.text),
  ]);
  return locale ? { percent, locale, fundingType } : undefined;
}

// Rewrites the old split-discount copy ("80% auf Challenges, 40% auf Instant
// Funding") into the unified offer. Deliberately a separate pass from
// withAffiliateDiscountOverrides, which early-returns while the affiliate
// toggle is off. heroChip2 is skipped on purpose: it is stored but never
// rendered anywhere.
//
// heroContent is the one field replaced outright instead of clause-rewritten —
// see newOfferHeroContent. Anything the page appended to it (e.g. "Und die
// Möglichkeit für $1 zu starten!") goes with it.
function withNewOfferCopy(
  data: PageAffiliateOldDesignSchema,
  offer: NewOffer | undefined,
): PageAffiliateOldDesignSchema {
  if (!offer) return data;
  const { percent, locale, fundingType } = offer;

  return {
    ...data,
    heroChip1: data.heroChip1
      ? {
          ...data.heroChip1,
          text: rewriteOfferChip(data.heroChip1.text, percent, locale),
        }
      : data.heroChip1,
    heroContent: newOfferHeroContent(percent, locale, fundingType),
    exclusiveItems: data.exclusiveItems
      ? data.exclusiveItems.map((item) => ({
          ...item,
          text: rewriteOfferListItem(item.text, percent, locale),
        }))
      : data.exclusiveItems,
    stepsItems: data.stepsItems
      ? data.stepsItems.map((item) => ({
          ...item,
          text: rewriteCouponRichtext(item.text, percent, locale),
        }))
      : data.stepsItems,
    lastText: rewriteOfferRichtext(data.lastText, percent, locale),
  };
}

export default function PageAffiliateOldDesignJSX({
  data: rawData,
}: {
  data: PageAffiliateOldDesignSchema;
}) {
  const discountedData = withAffiliateOfferDiscount(rawData);
  const newOffer = resolveNewOffer(discountedData);
  const data = withNewOfferCopy(
    withAffiliateDiscountOverrides(discountedData),
    newOffer,
  );
  // The launch widget and the competition pop-up share the bottom-left
  // corner; the launch campaign wins while it runs, as on the homepage.
  const { live: launchEventLive } = useLaunchEventLive(
    data?.launchEvent,
    data?.settings?.language,
  );

  // Most affiliate pages are single-asset: a crypto page must never surface
  // futures plans and vice-versa, so the chosen asset also hides the tabs.
  // "Both" — and an empty field — keeps every asset behind the tabs instead.
  const pricingAssetKey = data?.pricingSectionV2AssetKey;
  const pricingFilter =
    pricingAssetKey && pricingAssetKey !== "both"
      ? { assetKey: pricingAssetKey }
      : undefined;

  const affiliateCtaUrl =
    data?.heroCTA?.cta?.link?.url ??
    "https://checkout.iqcapital.io/products?coupon=50FUNDING";

  // Deliberately the hero URL itself, not `affiliateCtaUrl`: if a page has no
  // hero CTA we keep each tier's own product link rather than pointing every
  // card at the generic no-`aff=` fallback coupon.
  const pricingSection = withAffiliatePricingCta(
    data?.pricingSectionV2,
    data?.heroCTA?.cta?.link?.url,
  );

  return (
    <>
      <main className="">
        {data?.summerSale?.isActive && !data?.settings?.hideSummerSale && (
          <SummerSaleTopBanner
            data={data.summerSale}
            ctaUrl={affiliateCtaUrl}
            language={data.settings?.language}
          />
        )}
        {data?.summerSale?.isActive && !data?.settings?.hideSummerSale && (
          <SummerSalePopup
            data={data.summerSale}
            ctaUrl={affiliateCtaUrl}
            language={data.settings?.language}
          />
        )}
        {data?.birthdayBanner?.isActive &&
          !data?.settings?.hideBirthdayBanner && (
            <BirthdayTopBanner
              data={data.birthdayBanner}
              ctaUrl={affiliateCtaUrl}
              language={data.settings?.language}
            />
          )}
        {data?.birthdayBanner?.isActive &&
          !data?.settings?.hideBirthdayBanner && (
            <BirthdayPopUp
              data={data.birthdayBanner}
              ctaUrl={affiliateCtaUrl}
              language={data.settings?.language}
            />
          )}
        <AffiliateBanners
          settings={data?.settings}
          ctaUrl={affiliateCtaUrl}
          enableFlashSale
        />
        <LaunchEventWidget
          data={data?.launchEvent}
          language={data?.settings?.language}
          affCtaUrl={affiliateCtaUrl}
        />
        {!launchEventLive && (
          <TradingCompetitionPopup
            data={data?.tradingCompetitionPopup}
            language={data?.settings?.language}
            affCtaUrl={affiliateCtaUrl}
          />
        )}
        {data?.settings.header && (
          <NavBarV2 data={data?.settings.header} headerType="lp" />
        )}
        <Hero
          data={data}
          newOfferBadge={
            newOffer
              ? newOfferBadgeText(newOffer.percent, newOffer.locale)
              : undefined
          }
        />

        <ExclusiveSection
          data={data}
          className={`${data?.showReviewButton ? "pb-[7rem] lg:pb-[20rem]" : "py-[7rem] lg:py-[20rem]"}`}
        />
        {data?.pricingSectionV3 ? (
          <NewPricingSection
            sectionV3={data.pricingSectionV3}
            section={data.pricingSectionV2}
            language={data.settings?.language}
            fixedAsset={pricingAssetKey && pricingAssetKey !== "both" ? pricingAssetKey : undefined}
            checkoutHrefOverride={data?.heroCTA?.cta?.link?.url}
          />
        ) : pricingSection ? (
          <section className="relative w-full lg:px-0 px-[2rem]">
            <div className="mx-auto w-[min(170.625rem,100%)] flex flex-col gap-[6rem]">
              <PricingHeroBlock
                data={pricingSection}
                language={data.settings?.language}
              />
              <PricingTiersBlock
                data={pricingSection}
                language={data.settings?.language}
                discount={data?.pricingV2Discount}
                filter={pricingFilter}
              />
              {pricingSection.comparison &&
              pricingSection.specsCatalog?.length ? (
                <PricingComparisonBlock
                  data={pricingSection}
                  language={data.settings?.language}
                  discount={data?.pricingV2Discount}
                  filter={pricingFilter}
                />
              ) : null}
              {pricingSection.platformsFooter && (
                <PricingPlatformsBlock
                  data={pricingSection.platformsFooter}
                  language={data.settings?.language}
                  filter={pricingFilter}
                />
              )}
            </div>
          </section>
        ) : (
          <Deals data={data} />
        )}

        <Rules data={data} />

        <Payouts data={data} />
        <div className="w-full px-[2rem]">
          <div className="bg-[#FCFDFD] border border-[#E5E5E580] rounded-[1.5rem] lg:rounded-[5rem] w-[min(230rem,100%)] mx-auto">
            <ReviewsSection
              reviewsTitle={data.reviewsTitle}
              reviewsDocument={{
                _id: "inline",
                name: "Reviews",
                reviews: data.reviewsItems ?? [],
              }}
              fadeColor="white"
              slug={data?.settings?.slug?.current}
              language={data?.settings?.language}
              cardTheme="white"
            />
          </div>
        </div>

        <StepsSection data={data} />

        <LastCTACompetitionSection
          chip={data?.lastChip}
          text={data?.lastText}
          cta={data?.lastCTA}
          partnerName={data.heroPartner.text}
          partnerImage={data.heroPartner.photo}
          toggleCountdown={false}
          textClassName=" w-[min(128rem,100%)] prose-h2:w-full prose-h2:[text-wrap-style:balance] [&_br]:hidden lg:[&_br]:block prose-h2:mb-[2rem]"
        />
        {data?.settings?.footer && (
          <OldFooter
            data={data.settings.footer}
            language={data?.settings?.language}
          />
        )}
      </main>
    </>
  );
}
